import React, { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { MenuClient } from "../MenuClient"
import { Header } from "../../common/header/Header"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { useParams } from "react-router-dom"
import { BasisTab } from "./tabs/BasisTab"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { IErrorModel } from "../../../types/error/IErrorModel"
import { ContractorTab } from "./tabs/ContractorTab"
import { DocsTab } from "./tabs/DocsTab"
import { CRTab } from "./tabs/CRTab"
import { PaymentTab } from "./tabs/PaymentTab"
import { ICurrencyReplenishmentDto } from "@/types/replenishment/ICurrencyReplenishmentDto"

enum LocalStage {
  Basis = "BASIS",
  Contractor = "CONTRACTOR",
  Docs = "DOCS",
  CR = "CR",
  Payment = "PAYMENT",
  Done = "DONE"
}

export const CurrencyReplenishmentClient = () => {
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [errors, setErrors] = useState<IErrorModel>()
  const params = useParams()
  const [stage, setStage] = useState<LocalStage>(LocalStage.Basis)
  const [replenishment, setReplenishment] = useState<ICurrencyReplenishmentDto>({ stage: LocalStage.Basis })
  const [sending, setSending] = useState(false)

  const isNew = params.id === "new"

  useEffect(() => {
    if (params.id !== "new") {
      TrinityAPI.getCurrencyReplenishment(Number(params.id), response => {
        if (response.status === 200) {
          setReplenishment(response.data)

          if (response.data.status === "DONE") {
            setStage(LocalStage.Done)
          } else if (response.data.stage === "NEW" || response.data.stage === LocalStage.Basis) {
            setStage(LocalStage.Contractor)
          } else {
            setStage(response.data.stage as LocalStage)
          }
        }
      })
    } else {
      setStage(LocalStage.Basis)
    }
  }, [])

  const submitBasis = () => {
    if (sending) {
      return
    }
    setSending(true)
    TrinityAPI.createClientCurrencyReplenisment({ ...replenishment, stage: "NEW" }, response => {
      if (response.status === 200) {
        window.location.href = `/currency-replenishment/${response.data.id}`
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const submitContractor = () => {
    setStage(LocalStage.Docs)
  }

  const submitDocs = () => {
    if (sending) {
      return
    }
    setSending(true)
    TrinityAPI.submitCurrencyDocs(
      {
        ...replenishment,
        invoiceAmount: replenishment.currencyRequest?.invoicePrice,
        stage: LocalStage.Payment
      },
      response => {
        if (response.status === 200) {
          window.location.reload()
        } else {
          setErrors(response.data)
          setSending(false)
        }
      }
    )
  }

  const crAcceptance = (accepted: boolean) => {
    if (!replenishment.id) return
    TrinityAPI.crConfirmation(replenishment.id, accepted, response => {
      if (response.status === 200) {
        window.location.reload()
      } else {
        setModalMessage({ title: "Ошибка!", text: `Произошла ошибка отправки запроса: ${response.data.message}` })
      }
    })
  }

  const rejectPayment = () => {
    crAcceptance(false)
  }

  const submitBill = () => {
    if (sending) {
      return
    }
    setSending(true)
    TrinityAPI.submitCurrencyDocs(
      {
        ...replenishment,
        status: "SLIP_SENT"
      },
      response => {
        if (response.status === 200) {
          window.location.reload()
        } else {
          setErrors(response.data)
          setSending(false)
        }
      }
    )
  }

  const isPickable = (item: string) => {
    switch (item) {
      case "DOCS":
        return !!replenishment.status
      case "CONTRACTOR":
        return !!replenishment.status
      case "COMPANY":
        return replenishment.status && replenishment.status !== "NEW" && replenishment.status !== "DOC_NAC"
      case "CR":
        return (
          replenishment.status &&
          replenishment.status !== "NEW" &&
          replenishment.status !== "DOC_NAC" &&
          replenishment.status !== "DOC"
        )
      case "PAYMENT":
        return (
          replenishment.status &&
          ((replenishment.status === "NEW" && replenishment.invoiceAmount) ||
            replenishment.status === "SLIP_SENT" ||
            replenishment.status === "DONE")
        )
      case "DONE":
        return replenishment.status === "DONE"
    }
  }

  const getItemClazz = (item: string) => {
    if (item === "DONE" && stage === "DONE" && replenishment.status === "DONE") {
      return "deals-stage-item-active"
    } else if (item === "DONE") {
      return !isPickable(item) ? "deals-stage-item-lock" : "deals-stage-item"
    }
    if (item === "PAYMENT" && stage === "PAYMENT") {
      return "deals-stage-item-active"
    }
    if (item === stage) {
      return "deals-stage-item-active"
    } else {
      return !isPickable(item) ? "deals-stage-item-lock" : "deals-stage-item"
    }
  }

  if (!replenishment.id && !isNew) {
    return null
  }

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      <MenuClient active="currency-replenishment" />
      <section className="deals-info" style={{ display: "block" }}>
        <Header clazzName={"header-main-deals"} />
        <div className="container">
          <h1 className="ui-heading">
            Валютное пополнение {replenishment.id ? `№${replenishment.id}` : ""} на основании запроса №
            {replenishment.clientCurrencyRequestId}
          </h1>
          <ul className="deal-stage-list">
            <li
              className={`deals-stage-item${stage === LocalStage.Basis ? "-active" : ""}`}
              onClick={() => setStage(LocalStage.Basis)}
            >
              Основание
            </li>
            <li
              className={getItemClazz("CONTRACTOR")}
              style={!isPickable("CONTRACTOR") ? { pointerEvents: "none" } : {}}
              onClick={() => (isPickable("CONTRACTOR") ? setStage(LocalStage.Contractor) : void 0)}
            >
              Данные
            </li>
            <li
              className={getItemClazz("DOCS")}
              style={!isPickable("DOCS") ? { pointerEvents: "none" } : {}}
              onClick={() => (isPickable("DOCS") ? setStage(LocalStage.Docs) : void 0)}
            >
              Документы
            </li>
            <li
              className={getItemClazz("PAYMENT")}
              style={!isPickable("PAYMENT") ? { pointerEvents: "none" } : {}}
              onClick={() => (isPickable("PAYMENT") ? setStage(LocalStage.Payment) : void 0)}
            >
              Оплата
            </li>
            <li
              className={getItemClazz("DONE")}
              style={!isPickable("DONE") ? { pointerEvents: "none" } : {}}
              onClick={() => (isPickable("DONE") ? setStage(LocalStage.Done) : void 0)}
            >
              Фиксация
            </li>
          </ul>
        </div>

        {stage === LocalStage.Basis && (
          <BasisTab
            replenishment={replenishment}
            setReplenishment={setReplenishment}
            errorModel={errors}
            sending={sending}
            submit={submitBasis}
            isNew={isNew}
          />
        )}
        {stage === LocalStage.Contractor && (
          <ContractorTab
            replenishment={replenishment}
            setReplenishment={setReplenishment}
            errorModel={errors}
            sending={sending}
            submit={submitContractor}
          />
        )}
        {stage === LocalStage.Docs && (
          <DocsTab
            replenishment={replenishment}
            setReplenishment={setReplenishment}
            errorModel={errors}
            sending={sending}
            submit={submitDocs}
          />
        )}
        {stage === LocalStage.Payment && (
          <PaymentTab
            replenishment={replenishment}
            setReplenishment={setReplenishment}
            errorModel={errors}
            sending={sending}
            submit={submitBill}
            reject={rejectPayment}
          />
        )}
        {stage === LocalStage.Done && replenishment.status === "DONE" && (
          <CRTab replenishment={replenishment} setReplenishment={setReplenishment} crAcceptance={crAcceptance} />
        )}
      </section>
    </>
  )
}
